import axios, { AxiosInstance } from 'axios';
import { UserProfile } from '../types';

export const getApiBaseUrl = (): string => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const host = window.location.hostname;
  const port = window.location.protocol === 'https:' ? window.location.port : 8181;
  return `${protocol}://${host}${port ? `:${port}` : ''}/api`;
};

const API_BASE_URL = getApiBaseUrl();

class APIClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getChampionships() {
    return this.client.get('/championships');
  }

  getChampionshipRounds(championshipId: string) {
    return this.client.get(`/results/championships/${championshipId}/rounds`);
  }

  getResultsSession(sessionId: string) {
    return this.client.get(`/results/sessions/${sessionId}`);
  }

  getDriverLaps(sessionId: string, driverId: string | number, carModel?: string) {
    return this.client.get(`/results/sessions/${sessionId}/drivers/${encodeURIComponent(String(driverId))}/laps`, {
      params: carModel ? { carModel } : undefined,
    });
  }

  getSessionProgression(sessionId: string | number) {
    return this.client.get(`/results/sessions/${sessionId}/progression`);
  }

  getHotlapFilters() {
    return this.client.get('/results/hotlaps/filters');
  }

  getHotlaps(filters: { trackName?: string; trackLayoutName?: string; driverName?: string; carModel?: string; sessionType?: string; startDate?: string; endDate?: string }) {
    return this.client.get('/results/hotlaps', { params: filters });
  }

  getChampionshipWinner(championshipId: string) {
    return this.client.get(`/results/championships/${encodeURIComponent(championshipId)}/winner`);
  }

  getPracticeBestLaps(serverId: string, trackName?: string, trackLayoutName?: string) {
    return this.client.get(`/results/practice/${encodeURIComponent(serverId)}/best-laps`, {
      params: { trackName, trackLayoutName },
    });
  }

  getPracticeDriverLaps(serverId: string, driverId: number, trackName?: string, trackLayoutName?: string) {
    return this.client.get(`/results/practice/${encodeURIComponent(serverId)}/drivers/${encodeURIComponent(String(driverId))}/laps`, {
      params: { trackName, trackLayoutName },
    });
  }

  getDrivers(search?: string) {
    return this.client.get('/results/drivers', { params: { q: search } });
  }

  // Servers (may require backend to allow public access)
  getServers() {
    return this.client.get('/servers');
  }

  getDriver(driverId: string | number) {
    return this.client.get(`/results/drivers/${encodeURIComponent(String(driverId))}`);
  }

  getDriverBySteamId(steamId: string) {
    return this.client.get(`/results/drivers/steam/${encodeURIComponent(steamId)}`);
  }

  getCars() {
    return this.client.get('/assets/cars');
  }

  getServerLiveData(id: string) {
    return this.client.get(`/servers/${encodeURIComponent(id)}/live-data`);
  }

  getServerEndedLiveData(id: string) {
    return this.client.get(`/servers/${encodeURIComponent(id)}/ended-live-data`);
  }

  loginWithDiscord(profile: { discordUsername: string; avatarUrl?: string; youtube?: string; twitch?: string; steamId?: string }) {
    return this.client.post('/auth/discord/login', profile);
  }

  getCurrentUser(token: string) {
    return this.client.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  getPublicProfileBySteamId(steamId: string) {
    return this.client.get(`/auth/profiles/steam/${encodeURIComponent(steamId)}`);
  }

  updateUserProfile(token: string, updates: Partial<UserProfile>) {
    return this.client.put('/auth/profile', updates, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  getChampionshipRegistrations(championshipId: string) {
    return this.client.get(`/championships/${encodeURIComponent(championshipId)}/registrations`);
  }

  registerForChampionship(championshipId: string, payload: { carName: string; carNumber: string; agreedRules: boolean; carSelections?: Array<{ eventId: string; carName: string }> }, token: string) {
    return this.client.post(`/championships/${encodeURIComponent(championshipId)}/registrations`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  updateChampionshipRegistration(championshipId: string, registrationId: string, payload: { carName: string; carNumber: string; agreedRules: boolean; carSelections?: Array<{ eventId: string; carName: string }> }, token: string) {
    return this.client.put(`/championships/${encodeURIComponent(championshipId)}/registrations/${encodeURIComponent(registrationId)}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  withdrawChampionshipRegistration(championshipId: string, registrationId: string, token: string) {
    return this.client.delete(`/championships/${encodeURIComponent(championshipId)}/registrations/${encodeURIComponent(registrationId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  getChampionshipSignouts(championshipId: string) {
    return this.client.get(`/championships/${encodeURIComponent(championshipId)}/signouts`);
  }

  selfSignOut(championshipId: string, eventId: string, roundId: string, token: string) {
    return this.client.post(
      `/championships/${encodeURIComponent(championshipId)}/events/${encodeURIComponent(eventId)}/rounds/${encodeURIComponent(roundId)}/my-signout`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  selfSignBackIn(championshipId: string, eventId: string, roundId: string, token: string) {
    return this.client.delete(
      `/championships/${encodeURIComponent(championshipId)}/events/${encodeURIComponent(eventId)}/rounds/${encodeURIComponent(roundId)}/my-signout`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  getUpcomingCalendarEvents() {
    return this.client.get('/calendar/upcoming');
  }

  getRegisteredCalendarEvents(steamId: string) {
    return this.client.get(`/calendar/upcoming-registered/${encodeURIComponent(steamId)}`);
  }

  getCalendarIcsUrl(championshipId?: string): string {
    if (championshipId) {
      return `${API_BASE_URL}/calendar/ics/${encodeURIComponent(championshipId)}`;
    }
    return `${API_BASE_URL}/calendar/ics`;
  }
}

const api = new APIClient();
export default api;
